import React, { useState } from "react";
import {
  Box,
  Button,
  Link,
  Typography,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Slider,
} from "@mui/material";
import { ArrowRight } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import {
  getResponseMessageId,
  companySizeMap,
  companyExternalTools,
} from "bundles/common/utils/constants";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const selectSx = {
  borderRadius: "0.5rem",
  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
  fontSize: "0.875rem",
  "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
  "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
  "&.Mui-focused fieldset": {
    border: "2px solid hsl(153 68% 25%)",
  },
};

const labelSx = {
  color: "hsl(220, 10%, 46%)",
  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
  fontSize: "0.875rem",
  "&.Mui-focused": { color: "hsl(153 68% 25%)" },
};

export default function StepFour({ companyData, nextStep }) {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const authenticityToken = useSelector((state) => state.authenticity_token);

  const initialFormValues = {
    team_size: "",
    recruitment_plan: 0,
    use_external_tools: false,
    external_tools: [],
    recruitment_needs: 5,
  };

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [externalToolsSelectOpen, setExternalToolsSelectOpenLoading] =
    useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );

    if ("recruitment_plan" in fieldValues) {
      temp.recruitment_plan = fieldValues.recruitment_plan ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    let name = e.target.name;
    let value;

    switch (name) {
      case "use_external_tools":
        value = !(e.target.value === "true");
        break;
      default:
        value = e.target.value;
        break;
    }

    setExternalToolsSelectOpenLoading(false);

    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const handleSliderValue = (_e, value) => {
    setValues({
      ...values,
      ["recruitment_needs"]: value,
    });
  };

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    updateCompany();
  };

  const updateCompany = () => {
    if (validate(values)) {
      sh.put(`${i18nLocale}/company/${companyData.id}`, {
        authenticity_token: authenticityToken,
        company: {
          ...companyData,
          ...values,
        },
      })
        .then(() => {
          nextStep();
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.data?.message) {
            enqueueSnackbar(
              getResponseMessageId(err?.response?.data?.message),
              { variant: "error" }
            );
          }
          console.error("Error", err);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "hsl(0, 0%, 100%)",
        borderRadius: "1rem",
        boxShadow:
          "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
        p: { xs: "2rem", lg: "2.5rem" },
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}
    >
      {/* Title */}
      <Box
        component="h1"
        sx={{
          m: 0,
          fontFamily: "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
          fontSize: { xs: "1.5rem", lg: "1.875rem" },
          lineHeight: 1.2,
          color: "hsl(222.2, 84%, 4.9%)",
        }}
      >
        <FormattedMessage
          id="devise.signup.how_can_we_help_html"
          values={{
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      </Box>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {/* Company size */}
        <Box>
          <Box
            component="p"
            sx={{
              m: 0,
              mb: "0.5rem",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              lineHeight: 1.5,
            }}
          >
            <FormattedMessage
              id="company.company_size.description"
              values={{
                name: companyData.name,
                b: (chunks) => (
                  <span style={{ fontWeight: 600 }}>{chunks}</span>
                ),
              }}
            />
          </Box>
          <FormControl fullWidth variant="outlined" sx={inputSx}>
            <InputLabel sx={labelSx}>
              <FormattedMessage id="company.company_size.label" />
            </InputLabel>
            <Select
              id="team_size"
              name="team_size"
              value={values?.team_size}
              onChange={handleInputValue}
              label={<FormattedMessage id="company.company_size.label" />}
              sx={selectSx}
            >
              {companySizeMap.map((value) => (
                <MenuItem value={value} key={value}>
                  <FormattedMessage
                    id={"company.company_size.options." + value}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Recruitment plan */}
        <Box>
          <Box
            component="p"
            sx={{
              m: 0,
              mb: "0.5rem",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              lineHeight: 1.5,
            }}
          >
            <FormattedMessage
              id="company.recruitment_plan.description"
              values={{
                name: companyData.name,
                b: (chunks) => (
                  <span style={{ fontWeight: 600 }}>{chunks}</span>
                ),
              }}
            />
          </Box>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            InputProps={{ inputProps: { min: 0 } }}
            name="recruitment_plan"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            sx={inputSx}
            label={<FormattedMessage id="company.recruitment_plan.label" />}
            {...(errors["recruitment_plan"] && {
              error: true,
              helperText: errors["recruitment_plan"],
            })}
          />
        </Box>

        {/* External tools */}
        <Box>
          <Box
            component="p"
            sx={{
              m: 0,
              mb: "0.5rem",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              lineHeight: 1.5,
            }}
          >
            <FormattedMessage id="company.external_tools.description" />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              mb: values?.use_external_tools ? "0.75rem" : 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(220, 25%, 12%)",
              }}
            >
              <FormattedMessage id="company.external_tools.no_label" />
            </Typography>
            <Switch
              checked={values?.use_external_tools}
              value={values?.use_external_tools}
              onChange={handleInputValue}
              name="use_external_tools"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "hsl(153 68% 25%)",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "hsl(153 68% 25%)",
                },
              }}
            />
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(220, 25%, 12%)",
              }}
            >
              <FormattedMessage id="company.external_tools.yes_label" />
            </Typography>
          </Box>
          {values?.use_external_tools && (
            <FormControl fullWidth variant="outlined" sx={inputSx}>
              <InputLabel sx={labelSx}>
                <FormattedMessage id="company.external_tools.tool_label" />
              </InputLabel>
              <Select
                id="external_tools"
                name="external_tools"
                multiple
                required
                value={values?.external_tools}
                renderValue={(selected) =>
                  selected
                    .map((tool) =>
                      intl.formatMessage({
                        id: "company.external_tools.tool_options." + tool,
                      })
                    )
                    .join(", ")
                }
                onChange={handleInputValue}
                onOpen={() =>
                  setExternalToolsSelectOpenLoading(!externalToolsSelectOpen)
                }
                open={externalToolsSelectOpen}
                label={
                  <FormattedMessage id="company.external_tools.tool_label" />
                }
                sx={selectSx}
              >
                {companyExternalTools.map((value) => (
                  <MenuItem value={value} key={value}>
                    <FormattedMessage
                      id={"company.external_tools.tool_options." + value}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Recruitment needs slider */}
        <Box>
          <Box
            component="p"
            sx={{
              m: 0,
              mb: "0.75rem",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsl(220, 25%, 12%)",
              lineHeight: 1.5,
            }}
          >
            <FormattedMessage id="company.recruitment_needs.description" />
          </Box>
          <Box sx={{ px: "0.5rem" }}>
            <Slider
              defaultValue={1}
              value={values?.recruitment_needs}
              onChange={handleSliderValue}
              aria-labelledby="discrete-slider"
              valueLabelDisplay="on"
              step={1}
              marks
              min={0}
              max={10}
              sx={{
                color: "hsl(153 68% 25%)",
                "& .MuiSlider-thumb": {
                  bgcolor: "hsl(153 68% 25%)",
                  "&:hover, &.Mui-focusVisible": {
                    boxShadow: "0 0 0 8px hsl(153 68% 25% / 0.16)",
                  },
                },
                "& .MuiSlider-valueLabel": {
                  bgcolor: "hsl(153 68% 25%)",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.75rem",
                  borderRadius: "0.375rem",
                },
                "& .MuiSlider-mark": {
                  bgcolor: "hsl(220, 13%, 91%)",
                },
                "& .MuiSlider-rail": {
                  bgcolor: "hsl(220, 13%, 91%)",
                },
              }}
            />
          </Box>
        </Box>

        {/* Submit */}
        <Button
          fullWidth
          type="submit"
          disabled={loading}
          sx={{
            bgcolor: "hsl(153 68% 25%)",
            color: "white",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: "0.875rem",
            px: "1.5rem",
            py: "0.75rem",
            borderRadius: "0.75rem",
            textTransform: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            mt: "0.5rem",
            "&:hover": {
              bgcolor: "hsl(153 68% 25% / 0.9)",
            },
            "&.Mui-disabled": {
              bgcolor: "hsl(153 68% 25% / 0.3)",
              color: "hsl(0 0% 100% / 0.6)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            <>
              <FormattedMessage id="devise.signup.build_team" />
              <ArrowRight size={14} />
            </>
          )}
        </Button>
      </form>

      {/* Footer link */}
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "hsl(215.4, 16.3%, 46.9%)",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
        }}
      >
        <FormattedMessage id="devise.already_subscribed" />{" "}
        <Link
          href={`/${i18nLocale}/recruiters/sign_in`}
          sx={{
            color: "hsl(153, 68%, 25%)",
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <FormattedMessage id="signin" />
        </Link>
      </Typography>
    </Box>
  );
}
